<template>
  <div class="line-chart-container">
    <h4 v-if="title">{{ title }}</h4>
    <div class="line-chart"></div>
    <div class="legend-container">
      <div v-for="(site, i) in ['Actuel', ...sites]" :key="i" class="legend-item">
        <div class="legend-cercle" :class="{ dashed: i === 0 }" :style="{ background: colors[i] }"></div>
        <div class="legend-name">{{ site }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: ['sites', 'commuters', 'title', 'colors', 'chartWidth', 'chartHeight', 'chartMargin'],

  data() {
    const margin = this.chartMargin || { top: 0, right: 0, bottom: 50, left: 50 };
    return {
      margin,
      width: (this.chartWidth || 1780) - margin.left - margin.right,
      height: (this.chartHeight || 754) - margin.top - margin.bottom,
      duration: 700,
      min: 0,
      max: 0,
      colorScale: null,
      svg: null,
      x: null,
      y: null,
    };
  },

  watch: {
    commuters: {
      handler() {
        this.update();
      },

      deep: true,
    },
  },

  mounted() {
    this.circles = [];
    this.line = d3
      .line()
      .x(d => {
        return this.x(d[0]) + this.x.bandwidth() / 2;
      })
      .y(d => {
        return this.y(d[1]);
      })
      .curve(d3.curveMonotoneX);
    const { margin, width, height } = this;
    this.x = d3.scaleBand().range([0, width]).padding(0.1);
    this.getMinMax();
    this.xAxis = d3.axisBottom(this.x);
    this.y = d3.scaleLinear().range([height, 0]);
    this.yAxis = d3.axisLeft(this.y);
    this.svg = d3
      .select(this.$el.querySelector('.line-chart'))
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    // this.updateDomains()
    this.colorScale = d3.scaleLinear().domain([this.min, 0, this.max]).range(this.colors);

    // this.setBars()
    this.addLegend();
    this.update(true);
  },

  methods: {
    getMinMax() {
      const flatCommuters = this.commuters.reduce((acc, val) => acc.concat(val), []);
      this.min = d3.min(flatCommuters, d => {
        return d[1];
      });
      this.max = d3.max(flatCommuters, d => {
        return d[1];
      });
    },

    updateDomains() {
      this.x.domain(
        this.commuters[0].map(d => {
          return d[0];
        }),
      );
      this.y.domain([this.min >= 0 ? 0 : this.min, this.max]);
    },

    addLegend() {
      const { height } = this;
      this.svg
        .append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', 'translate(0,' + height + ')')
        .call(d3.axisBottom(this.x).tickSizeOuter(0));
      this.svg.append('g').attr('class', 'axis axis-y').call(d3.axisLeft(this.y).tickSizeOuter(0));
    },

    updateLines() {
      const { duration } = this;
      const splines = this.svg.selectAll('.spline').data(this.commuters, d => d.id);
      splines
        .enter()
        .append('path')
        .attr('class', 'spline')
        .style('stroke-dasharray', (d, i) => {
          return i === 0 ? '3, 9' : '0';
        })
        .attr('fill', 'none')
        .attr('stroke', d => {
          return d.color;
        })
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-width', 3)
        .attr('stroke-opacity', 0)
        .attr('d', d => {
          return this.line(d);
        })
        .merge(splines)
        .transition()
        .duration(duration)
        .attr('d', d => {
          return this.line(d);
        })
        .attr('stroke-opacity', 1);

      splines.exit().transition().duration(duration).attr('stroke-opacity', 0).remove();

      const circlesGroup = this.svg.selectAll(`.circle-group`).data(this.commuters, d => d.id);

      circlesGroup
        .enter()
        .append('g')
        .attr('class', 'circle-group')
        .attr('opacity', 1)
        .each((data, idx, a) => {
          const circles = d3
            .select(a[idx])
            .selectAll(`.circle`)
            .data(data, d => {
              return d[0];
            })
            .enter()
            .append('circle')
            .attr('class', `circle`)
            .attr('cy', d => {
              return this.y(d[1]);
            })
            .attr('cx', d => {
              return this.x(d[0]) + this.x.bandwidth() / 2;
            })
            .attr('r', 4.0)
            .attr('fill', 'white')
            .attr('stroke', () => data.color)
            .attr('stroke-width', 3)
            .attr('stroke-opacity', 0);
          circles
            .merge(circles)
            .transition()
            .duration(duration)
            .attr('cy', d => {
              return this.y(d[1]);
            })
            .attr('stroke-opacity', 1);
        });
      circlesGroup
        .merge(circlesGroup)
        .transition()
        .duration(duration)
        .attr('opacity', 1)
        .each((data, idx, a) => {
          const circles = d3
            .select(a[idx])
            .selectAll(`.circle`)
            .data(data, d => {
              return d[0];
            });
          circles
            .merge(circles)
            .transition()
            .duration(duration)
            .attr('cy', d => {
              return this.y(d[1]);
            });
        });
      circlesGroup.exit().transition().duration(duration).attr('opacity', 0).remove();
    },

    update() {
      const { duration } = this;
      this.getMinMax();
      this.updateDomains();

      this.updateLines();

      this.svg.select('.axis.axis-x').transition().duration(duration).call(this.xAxis);
      this.svg.select('.axis.axis-y').transition().duration(duration).call(this.yAxis);
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';

svg {
  overflow: visible;
}

:global(.tv) {
  .legend-container {
    margin-top: 32px;
    font-size: 32px;

    .legend-item .legend-cercle {
      width: 32px;
      margin: 0 12px 0 0;
    }
  }
}

.line-chart-container {
  flex: 1;
  h4 {
    margin-left: 35px;
  }
  & + .line-chart-container {
    margin-top: 30px;
  }
}
.domain,
.tick,
.tick line {
  stroke: colors.$earth-color;
}
.tick line {
  stroke: transparent;
}
.tick text {
  stroke: none;
  fill: colors.$white-color;
  font-size: 22px;
  transform: translateY(20px);
  font-family: 'Source Sans Pro', Helvetica, sans-serif;
}

.legend-container {
  padding: 4px 12px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;

  .legend-item {
    display: flex;
    align-items: center;
    margin: 0 8px 0 0;

    .legend-cercle {
      flex-shrink: 0;
      width: 16px;
      height: 2px;
      margin: 0 4px 0 0;
      border-radius: 2px;
      transform: rotate(-45deg);

      &.dashed {
        background: none !important;
        border-top: dashed 2px;
        border-radius: 0;
        height: 0;
      }
    }
    .legend-name {
    }
  }
}
</style>
